// =============================================================================
// io/sync/exportAccount.ts — "Download my data".
// -----------------------------------------------------------------------------
// ── 🚨 THE SHIPPING CLIENT'S TABLE LIST IS WRONG IN BOTH DIRECTIONS ──
//
// Its export card promises "a complete JSON copy of your account … everything in
// your cloud data". Checked against `supabase/migrations/` rather than copied:
//
//   IT QUERIES FOUR TABLES THAT DO NOT EXIST — `user_state`, `session_journal`,
//   `pattern_categories`, `screenshots`. Each appears exactly ONCE in that entire
//   file: in this list. `screenshots` is a storage BUCKET and a `TEXT[]` column,
//   never a table. All four land in the file as `{_error: …}` stubs.
//
//   AND IT OMITS NINE REAL ONES — `user_preferences` (every setting the user
//   has), `goals`, `commission_schedules`, `reviews`, `best_opps`, `backtests`,
//   `practice_sessions`, `practice_trades`, `ingest_keys`.
//
// ⚠ NOT REPRODUCED. This is the file the Danger Zone tells you to download
// BEFORE deleting your account — the one irreversible action in the product. An
// export that silently omits every setting you ever configured is a data-loss
// trap, not a parity target. Registered as a parity delta.
//
// ── THE RULE FOR WHAT GOES IN, SO THE NEXT PERSON CAN EXTEND IT CORRECTLY ──
//
//   IN  — every table whose RLS scopes rows to the user AND which holds their
//         CONTENT or CONFIGURATION.
//   OUT — shared reference data (`contracts`, `price_bars`) which is not theirs,
//         and operational telemetry (`coach_runs`, `bars_fetch_runs`,
//         `import_events`) which is metering rather than anything they made.
//
// ⚠ AND THE EXCLUSIONS ARE WRITTEN INTO THE FILE ITSELF, with reasons. A file
// whose entire job is "everything you have" must not be able to omit something
// silently — if a future table is missed, the reader can at least see the list
// it was judged against.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';
import { record } from '../diagnostics/errorLog';

/**
 * ⚠ `ingest_keys` is listed with an explicit COLUMN SET, and that is deliberate.
 *
 * The table holds `key_hash` — the sha-256 of a live ingest key — and its own
 * migration says "the SPA simply never SELECTs key_hash" because RLS is
 * row-scoped, not column-scoped. A `select *` would put secret-derived material
 * into a file that lands in the user's Downloads folder. The hash is not a usable
 * credential, and it still has no business being there.
 */
export interface ExportTable {
  table: string;
  /** `'*'` unless a column must be withheld — see above. */
  columns: string;
  /**
   * The column every page is ordered by — see `readAll`, which is where the
   * reason lives.
   *
   * ⚠ EACH IS THE TABLE'S REAL PRIMARY KEY, read out of `information_schema` on
   * the linked database (2026-09-08) rather than assumed: ordering by a column
   * that does not exist fails the whole export, and that failure would be a
   * complete one. Fourteen of the eighteen are `id`; the other four are not, and
   * three of those are COMPOSITE `(user_id, …)`. RLS scopes every row read here
   * to one user, so `user_id` is constant across the whole result and the other
   * half of the key orders it totally on its own.
   */
  orderBy: string;
}

export const EXPORT_TABLES: readonly ExportTable[] = [
  { table: 'accounts', columns: '*', orderBy: 'id' },
  { table: 'account_adjustments', columns: '*', orderBy: 'id' },
  { table: 'backtests', columns: '*', orderBy: 'id' },
  { table: 'best_opps', columns: '*', orderBy: 'id' },
  { table: 'commission_schedules', columns: '*', orderBy: 'firm_key' },
  { table: 'exit_models', columns: '*', orderBy: 'id' },
  { table: 'goals', columns: '*', orderBy: 'id' },
  {
    table: 'ingest_keys',
    columns: 'id,key_prefix,label,account_id,source,created_at,last_used_at,revoked_at',
    orderBy: 'id',
  },
  { table: 'journal_entries', columns: '*', orderBy: 'date' },
  { table: 'patterns', columns: '*', orderBy: 'id' },
  { table: 'practice_sessions', columns: '*', orderBy: 'id' },
  { table: 'practice_trades', columns: '*', orderBy: 'id' },
  { table: 'reviews', columns: '*', orderBy: 'id' },
  // ── 🔴 THE LINKS THE USER HANDED OUT, AND THEY WERE IN NEITHER LIST ──
  //
  // Not among the tables, not among the exclusions — so a file whose whole
  // promise is "everything you have, and here is what it leaves out" left this
  // out and did not say so. By the rule at the top of this file they are plainly
  // IN: RLS scopes them to the user and the user made them.
  //
  // ⚠ AND THEY OUTLIVE THE ACCOUNT IN A WAY NOTHING ELSE HERE DOES. A share is
  // served by an Edge Function reading this table, so "what did I send out, and
  // is it still live" is exactly what somebody about to press Delete needs
  // written down. `revoked_at` and `expires_at` are what make that readable.
  //
  // ⚠ `payload` IS WITHHELD, and for a different reason from `ingest_keys`'
  // hash below. It is the whole rendered report HTML for every share ever made —
  // a duplicate of trades and journal rows already in this same file, in a form
  // nothing can read back, and large enough to dominate the download. Named in
  // the exclusions, because withholding it silently is the defect being fixed.
  //
  // ⚠ ORDERED BY `token`, WHICH IS THE PRIMARY KEY. This table has no `id`
  // column at all (20260713120000_shared_reports.sql) — and ordering by a column
  // that does not exist fails the WHOLE export, not just this table.
  {
    table: 'shared_reports',
    columns: 'token,kind,created_at,expires_at,revoked_at',
    orderBy: 'token',
  },
  { table: 'snapshots', columns: '*', orderBy: 'id' },
  // ⚠ THE LEGACY BILLING TABLE, AND IT STAYS. Nothing in either client reads it
  // — `billing_subscriptions` is the live one — but it is RLS-scoped to the user
  // and may still hold their old row, and dropping a table from this list
  // silently removes rows from a file whose entire promise is completeness. The
  // live table is named in the exclusions below instead, with its reason.
  { table: 'subscriptions', columns: '*', orderBy: 'user_id' },
  { table: 'trades', columns: '*', orderBy: 'id' },
  // 🔴 ADDED 2026-09-08 — THIS LIST HAD NOT CAUGHT UP WITH THE SCHEMA. Per-user
  // instruments landed on 2026-09-06 (D256) and this file predates them, so a
  // user's own custom contracts were missing from the export the Danger Zone
  // tells them to take BEFORE deleting their account. That is exactly the
  // data-loss trap this file's own header describes, committed by the file that
  // describes it. ▶ A NEW USER-SCOPED TABLE MUST BE ADDED HERE.
  { table: 'user_contracts', columns: '*', orderBy: 'symbol' },
  { table: 'user_preferences', columns: '*', orderBy: 'user_id' },
];

/** Written into the file so nothing is missing without saying so. */
export const EXPORT_EXCLUSIONS: readonly { table: string; why: string }[] = [
  { table: 'contracts', why: 'shared instrument catalogue — the same rows for every user, not yours' },
  { table: 'price_bars, price_bars_1m', why: 'licensed market data, shared and not yours to take' },
  { table: 'coach_runs, bars_fetch_runs, import_events', why: 'usage metering and telemetry, not content you created' },
  { table: 'ingest_keys.key_hash', why: 'the hash of a live key — withheld from the file on purpose' },
  { table: 'shared_reports.payload', why: 'the rendered HTML of each shared report — a copy of trades and journal entries this file already contains, in a form nothing can read back. The links themselves ARE exported, with their created, expiry and revoked dates' },
  { table: 'billing_subscriptions', why: 'your subscription is held by the payment provider and restored from there — a copy here would be a stale second opinion about what you are paying for' },
  { table: 'storage: screenshots, recordings, snapshots', why: 'files, not rows — this export is JSON only, and the filenames it does contain name them' },
];

export const EXPORT_FORMAT = 'tradecoach-export-v1';

/** `tradecoach-export-2026-08-03.json` */
export const exportFilename = (nowIso: string): string =>
  `tradecoach-export-${nowIso.slice(0, 10)}.json`;

export interface ExportMeta {
  exported_at: string;
  user_id: string;
  email: string;
  exported_by: string;
  format: string;
  not_included: readonly { table: string; why: string }[];
}

export interface AccountExport {
  meta: ExportMeta;
  [table: string]: unknown;
}

/**
 * Read one table to exhaustion.
 *
 * ⚠ PAGED. An export that stops at the first 1,000 rows is the worst possible
 * failure here: it looks complete, it opens fine, and the trades are simply not
 * in it. Josh's book alone is past that mark.
 */
async function readAll(
  sb: SupabaseClient, spec: ExportTable,
): Promise<unknown[] | { _error: string }> {
  const rows: unknown[] = [];
  for (let from = 0; ; from += PAGE_ROWS) {
    // ── 🔴 `.order()` IS LOAD-BEARING HERE, NOT TIDINESS ──
    //
    // `range()` over an UNORDERED query has no defined page boundary. Postgres
    // may return rows in any order it likes, so a row inserted between two
    // requests shifts the window — DROPPING one row and DUPLICATING another, in
    // a file that looks perfectly complete and opens fine.
    //
    // ⚠ Not theoretical on this book: the ATAS/Sierra/MotiveWave loggers POST
    // trades live, so an export taken during a session is precisely that window.
    // `sync/load.ts` orders every paged read and says why; this file was written
    // without it.
    const { data, error } = await sb
      .from(spec.table)
      .select(spec.columns)
      .order(spec.orderBy, { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    // ⚠ The ERROR REPLACES THE TABLE, and does not abort the export. One
    // unreadable table must not cost the user the other sixteen — and the stub
    // is what tells them the file is short, which silence would not.
    //
    // ── 🚨 IT DOES NOT LOG HERE, AND THAT IS THE WHOLE POINT ──
    //
    // It did, briefly, and the effect was the opposite of the intent: the
    // failures that take out an export take out ALL of it — an expired session,
    // a revoked grant — so seventeen tables each filed a near-identical entry.
    // The log buffer holds 200 and survives that, but a FEEDBACK REPORT attaches
    // only the last ten, so a user who hit a failed export and pressed Send
    // feedback shipped ten copies of one message with everything before it
    // evicted. That is precisely the field this migration exists to make useful.
    //
    // ▶ `buildAccountExport` files ONE entry naming every table that failed.
    if (error) return { _error: error.message };
    if (!data || !data.length) break;
    rows.push(...(data as unknown[]));
    if (data.length < PAGE_ROWS) break;
  }
  return rows;
}

/**
 * Build the whole export.
 *
 * @param now an ISO timestamp. Injected rather than read here so a test pins the
 *   stamp — `domain/` has no clock and neither should this.
 */
export async function buildAccountExport(
  sb: SupabaseClient,
  user: { id: string; email: string },
  now: string,
  /**
   * ⚠ The CLIENT, and a version when there is one. This client ships no version
   * string yet, so callers pass `'tradecoach-app'` — writing `0.0.0` from
   * package.json would look like a real release number and mean nothing. When a
   * version exists it is appended here and nothing else changes.
   */
  exportedBy: string,
): Promise<AccountExport> {
  const out: AccountExport = {
    meta: {
      exported_at: now,
      user_id: user.id,
      email: user.email,
      // ⚠ Names WHICH client wrote it. Both clients emit `format: v1`, and their
      // table sets differ — so without this a reader cannot tell a file missing
      // `user_preferences` because it is old from one missing it because
      // something broke.
      exported_by: exportedBy,
      format: EXPORT_FORMAT,
      not_included: EXPORT_EXCLUSIONS,
    },
  };

  // ⚠ SEQUENTIAL, not `Promise.all`. Seventeen tables paged concurrently is a
  // burst of parallel range queries against the user's own rate limit, and the
  // export is a once-in-a-while action where finishing reliably beats finishing
  // fast.
  for (const spec of EXPORT_TABLES) {
    try {
      out[spec.table] = await readAll(sb, spec);
    } catch (e) {
      // ⚠ No log here either — see `readAll`. One entry for the whole export,
      // below.
      out[spec.table] = { _error: e instanceof Error ? e.message : 'read failed' };
    }
  }

  // ── ONE ENTRY FOR THE WHOLE EXPORT, naming what is missing ──
  //
  // ⚠ This is the copy someone takes BEFORE deleting their account, so a short
  // file is the expensive kind of quiet — the stub in the JSON only helps
  // somebody who opens it and reads it. But the log line has to be worth the
  // slot it takes in a feedback report, which means one line that names every
  // missing table rather than seventeen that each name one.
  const failed = failedTables(out);
  if (failed.length) {
    record({
      kind: 'error',
      msg: `export account: ${failed.length} of ${EXPORT_TABLES.length} tables could not be read `
        + `(${failed.join(', ')})`,
      stack: null,
      source: null,
    });
  }

  return out;
}

/** Every table that came back as an error stub, for the caller to report. */
export function failedTables(out: AccountExport): string[] {
  return EXPORT_TABLES
    .map((s) => s.table)
    .filter((t) => {
      const v = out[t];
      return !!v && typeof v === 'object' && !Array.isArray(v) && '_error' in (v as object);
    });
}

/** Total rows across every table that read cleanly. */
export function exportRowCount(out: AccountExport): number {
  return EXPORT_TABLES.reduce((n, s) => {
    const v = out[s.table];
    return n + (Array.isArray(v) ? v.length : 0);
  }, 0);
}
